import { useEffect, useState } from "react";
import { adminApi } from "../services/api";
import { useAuthStore } from "../stores/authStore";

const ROLE_LABELS: Record<string, string> = { admin: "Admin", support_l2: "Support L2", support_l1: "Support L1" };
const ROLE_BADGES: Record<string, string> = { root: "badge-root", admin: "badge-admin", support_l2: "badge-l2", support_l1: "badge-l1" };

export default function Team() {
  const { hasPermission, role: myRole } = useAuthStore();
  const [members,     setMembers]     = useState<any[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole,  setInviteRole]  = useState("support_l1");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { const r = await adminApi.listTeam(); setMembers(r.members); }
    finally { setLoading(false); }
  }

  async function invite() {
    if (!inviteEmail) return;
    setSaving(true); setError("");
    try { await adminApi.inviteTeam(inviteEmail, inviteRole); setInviteEmail(""); await load(); }
    catch (e: any) { setError(e.status === 403 ? "You cannot invite at that role level." : "Failed to invite."); }
    finally { setSaving(false); }
  }

  async function revoke(memberId: string, email: string) {
    if (!window.confirm(`Revoke access for ${email}?`)) return;
    await adminApi.revokeTeam(memberId);
    await load();
  }

  if (!hasPermission("team:invite")) {
    return <div className="card" style={{ color: "var(--text-muted)" }}>team:invite permission required.</div>;
  }

  const invitableRoles = myRole === "root" ? ["admin", "support_l2", "support_l1"] : ["support_l2", "support_l1"];

  return (
    <div style={{ maxWidth: 700, display: "flex", flexDirection: "column", gap: 20 }}>
      <h1 style={{ fontSize: 20, fontWeight: 900 }}>🎧 Team</h1>

      <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>Invite Member</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="email" placeholder="email@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
            style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text)", fontSize: 13 }} />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text)", fontSize: 13 }}>
            {invitableRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <button className="btn btn-primary" onClick={invite} disabled={saving || !inviteEmail}>{saving ? "Inviting…" : "Invite"}</button>
        </div>
        {error && <div style={{ color: "#f87171", fontSize: 12 }}>{error}</div>}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--surface2)", color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em" }}>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Email</th>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Role</th>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Status</th>
              <th style={{ padding: "9px 14px", textAlign: "left" }}>Last Login</th>
              <th style={{ padding: "9px 14px" }} />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.memberId} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ padding: "9px 14px" }}>{m.email}</td>
                <td style={{ padding: "9px 14px" }}><span className={`badge ${ROLE_BADGES[m.role] ?? "badge-l1"}`}>{m.role}</span></td>
                <td style={{ padding: "9px 14px", color: "var(--text-muted)" }}>{m.status}</td>
                <td style={{ padding: "9px 14px", color: "var(--text-muted)", fontFamily: "monospace", fontSize: 11 }}>{m.lastLoginAt?.slice(0,10) ?? "—"}</td>
                <td style={{ padding: "9px 14px", textAlign: "right" }}>
                  {m.role !== "root" && m.status !== "revoked" && hasPermission("team:revoke") && (
                    <button className="btn btn-danger" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => revoke(m.memberId, m.email)}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
